/* <variant-picker>: maps option radios to a variant, updates URL, hidden id,
   price, media and buttons via the Section Rendering API. */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.addEventListener('change', this.onChange);
    this.markAvailability();
  }

  onChange = () => {
    const variant = this.currentVariant();
    this.updateSelectedLabels();
    this.markAvailability();
    this.updateHiddenInput(variant);
    this.updateUrl(variant);
    this.renderSection(variant);
  };

  selectedOptions() {
    const groups = [...this.querySelectorAll('fieldset')];
    return groups.map((group) => group.querySelector('input:checked')?.value ?? null);
  }

  currentVariant() {
    const selected = this.selectedOptions();
    return this.variants.find((variant) => variant.options.every((value, index) => value === selected[index])) ?? null;
  }

  updateSelectedLabels() {
    this.querySelectorAll('fieldset').forEach((group) => {
      const label = group.querySelector('[data-selected-value]');
      const checked = group.querySelector('input:checked');
      if (label && checked) label.textContent = checked.value;
    });
  }

  /* Strike through values that have no available variant given the other selected options. */
  markAvailability() {
    const selected = this.selectedOptions();
    this.querySelectorAll('input[type="radio"]').forEach((input) => {
      const index = Number(input.dataset.optionIndex);
      const candidate = [...selected];
      candidate[index] = input.value;
      const match = this.variants.find((variant) =>
        variant.options.every((value, i) => candidate[i] === null || value === candidate[i]),
      );
      const available = Boolean(match && match.available);
      input.nextElementSibling.classList.toggle('variant-picker__value--unavailable', !available);
    });
  }

  updateHiddenInput(variant) {
    const input = document.getElementById(`ProductVariantId-${this.sectionId}`);
    if (!input) return;
    input.value = variant ? variant.id : '';
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  updateUrl(variant) {
    if (!variant) return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
  }

  /* Every Add to Cart and Buy now button bound to this section: the main pair
     and the sticky bar's, which submit the same form through the form attribute. */
  submitButtons() {
    return [
      ...document.querySelectorAll(`[data-add-button="${this.sectionId}"], [data-buy-now="${this.sectionId}"]`),
    ];
  }

  async renderSection(variant) {
    const buttons = this.submitButtons();
    if (!variant) {
      buttons.forEach((button) => {
        button.disabled = true;
        const label = button.querySelector('[data-add-label]');
        if (label) label.textContent = window.variantStrings.unavailable;
      });
      return;
    }
    const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
    if (variant.featured_media && gallery) gallery.setActiveMedia(String(variant.featured_media.id));

    const url = `${this.dataset.url}?variant=${variant.id}&section_id=${this.sectionId}`;
    const response = await fetch(url);
    const html = await response.text();

    // Price blocks in the panel and the sticky bar, and the sticky bar's thumbnail.
    ['Price', 'StickyPrice', 'StickyMedia'].forEach((prefix) => {
      const id = `${prefix}-${this.sectionId}`;
      const fresh = window.OlyJoy.parseSectionHTML(html, `#${id}`);
      const current = document.getElementById(id);
      if (fresh && current) current.replaceWith(fresh);
    });

    const freshButton = window.OlyJoy.parseSectionHTML(html, `#ProductSubmit-${this.sectionId}`);
    if (freshButton) {
      const freshLabel = freshButton.querySelector('[data-add-label]').textContent;
      buttons.forEach((button) => {
        button.disabled = freshButton.disabled;
        const label = button.querySelector('[data-add-label]');
        if (label) label.textContent = freshLabel;
      });
    }
  }
}
customElements.define('variant-picker', VariantPicker);
